import { Gauge, Target, Warning } from "@phosphor-icons/react";

/**
 * The three workstreams a reporting year is divided into, in display order.
 *
 * The strings must match the backend's `FoundationV2QuestionType.groupName()` exactly — they arrive on each
 * question as `questionGroup` and are what the server keys a target structure on. They are also v1's own group
 * names, so the two modules section their pages by the same headings.
 *
 * Descriptions are NOT held here: they come from `question.description`, which the server sets from the type.
 * A copy client-side would drift from what the answer page and the activity feed call the same question.
 */
export const FOUNDATION_SECTION_ORDER = ["GHG Emissions", "Risks and Opportunities", "Targets"];

export const FOUNDATION_SECTION_ICONS = {
  "GHG Emissions": Gauge,
  "Risks and Opportunities": Warning,
  Targets: Target,
};

/**
 * Groups questions by workstream, known ones first and in order, then anything else.
 *
 * An unrecognised group gets its own section rather than being dropped: a question this build does not know
 * about is still a question somebody has to answer, and silently hiding it is how work goes missing.
 */
export function groupQuestionsBySection(questions) {
  const byGroup = new Map();
  (questions || []).forEach((question) => {
    const key = question.questionGroup || "Ungrouped";
    if (!byGroup.has(key)) byGroup.set(key, []);
    byGroup.get(key).push(question);
  });

  const known = FOUNDATION_SECTION_ORDER.filter((key) => byGroup.has(key)).map((key) => ({
    name: key,
    questions: byGroup.get(key),
  }));
  const unknown = [...byGroup.keys()]
    .filter((key) => !FOUNDATION_SECTION_ORDER.includes(key))
    .map((key) => ({ name: key, questions: byGroup.get(key) }));

  return [...known, ...unknown];
}

/** The 4-digit year in a question's name, which is how it was assembled. */
export function yearOfQuestion(question) {
  return parseInt(question?.question?.match(/\d{4}/)?.[0], 10) || 0;
}
